import type {
  IAdViewPremium,
  ICarLeasingAdView,
  ICarLeasingBusinessAdView,
  ICarNewAdView,
  ICarUsedAdView,
  ICarUsedBusinessAdView,
} from "@finn-no/adview-provider-frontend-types";
import type { MobilityAd } from "@mobility-item-page/types";
import { isCarAd } from "@mobility-item-page/utilities";
import type { PersonalFinanceContent } from "src/types/personalFinance.ts";
import { SafetyTabs } from "../types/safetyElements.ts";

export const hasDeclartionOfGoods = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return "declaration_of_goods" in ad;
};

export const hasExchangePolicyInfo = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return !!("right_to_exchange_info" in ad && ad.right_to_exchange_info);
};

export const hasHomeDelivery = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView | ICarNewAdView => {
  return !!("home_delivery" in ad && ad.home_delivery);
};

export const hasServiceHistoryInfo = (
  ad: MobilityAd,
): ad is ICarUsedAdView | ICarUsedBusinessAdView | ICarLeasingAdView | ICarLeasingBusinessAdView => {
  return !!("service_history_info" in ad && ad.service_history_info && ad.service_history_info.description);
};

export const hasWarranty = (ad: MobilityAd): boolean => {
  if (!("warranty" in ad) || !ad.warranty) {
    return false;
  }
  // Caravan ads model warranty as a plain boolean, so there are no limits to check
  if (ad.warranty === true) {
    return true;
  }
  return ad.warranty.distance !== 0 && ad.warranty.duration !== 0;
};

export const getMobilitySafetyElementTabs = (
  ad: MobilityAd | undefined,
  usedCarGuaranteeSafetyElement: PersonalFinanceContent | null,
): SafetyTabs[] => {
  if (!ad) {
    return [];
  }

  const tabs: SafetyTabs[] = [];
  if (usedCarGuaranteeSafetyElement) {
    tabs.push(SafetyTabs.UsedCarWarrantyPurchased);
  } else if (hasWarranty(ad)) {
    tabs.push(SafetyTabs.Warranty);
  }
  if (hasDeclartionOfGoods(ad)) {
    tabs.push(SafetyTabs.DeclarationOfGoods);
  }

  if (isCarAd(ad) && "condition_report" in ad && ad.condition_report?.link) {
    tabs.push(SafetyTabs.Condition);
  }
  if ("service_plan_followed" in ad && ad.service_plan_followed) {
    tabs.push(SafetyTabs.ServicePlanFollowed);
  }
  if (hasServiceHistoryInfo(ad)) {
    tabs.push(SafetyTabs.ServiceHistory);
  }
  if (hasExchangePolicyInfo(ad)) {
    tabs.push(SafetyTabs.ExchangePolicy);
  }
  if ("premium" in ad && (ad.premium as IAdViewPremium).url) {
    tabs.push(SafetyTabs.Premium);
  }
  if (hasHomeDelivery(ad)) {
    tabs.push(SafetyTabs.HomeDelivery);
  }

  return tabs;
};
